import React from 'react';
import Router from 'next/router';
import PropTypes from 'prop-types';
import { parse, stringify } from 'qs';

import { decodeState } from '@pcid/request-utils';
import LoadingPage from '@pcid/components/loading-page';
import Layout from '@pcid/components/layout';
import Link from '@pcid/link';
import oops from '@pcid/utils/oops';
import { logErrorCase } from '@pcid/utils/form-utils';
import { storageManager } from '@pcid/storage-manager';
import { withFeatureFlags, withRelyingPartyConfig } from '@pcid/components/config-context';
import { AutoFormattedMessage, autoFormatMessage } from '@pcid/string-utils';

import { getPageViewPayload } from '../../actions/track-analytics';
import track, { trackSnowplowEvent } from '@pcid/analytics/track';

const utmKeys = [
	'utm_source',
	'utm_medium',
	'utm_campaign',
	'utm_term',
	'utm_content',
	'UTM_SOURCE',
	'UTM_MEDIUM',
	'UTM_CAMPAIGN',
	'UTM_TERM',
	'UTM_CONTENT',
];

/**
 * Next.js Router.query is empty on initial client-side mount and is populated asynchronously.
 * Use the URL as fallback so we don't treat a valid success URL as missing redirectURL and redirect to /oops.
 */
function getQuery() {
	if (typeof window === 'undefined') return Router.query;
	const query = Router.query;
	if (query && Object.keys(query).length > 0) return query;
	return parse(window.location.search.slice(1) || '');
}

class LoginSuccess extends React.Component {
	componentDidMount() {
		// Track page view for login success
		const { gtmPayload, snowplowPayload } = getPageViewPayload('/login/success');
		if (gtmPayload) {
			track.pageView(gtmPayload);
		}
		if (snowplowPayload) {
			trackSnowplowEvent(snowplowPayload);
		}

		const query = getQuery();
		const { redirectURL } = query;
		const routerQueryEmpty = typeof window !== 'undefined'
			&& (!Router.query || Object.keys(Router.query).length === 0);

		if (this.showConfirmLogin()) {
			return;
		}

		if (typeof redirectURL === 'string') {
			if (routerQueryEmpty) {
				logErrorCase({
					event: 'loginSuccess.queryFromUrlFallback',
					page: 'login/success',
					message: 'Router.query was empty on mount; used window.location.search',
				});
			}
			storageManager.clearSession();
			window.location.replace(this.getRedirectURL());
		} else {
			logErrorCase({
				event: 'loginSuccess.redirectToOops',
				page: 'login/success',
				message: 'Missing redirectURL in query; redirecting to /oops',
				routerQueryEmpty,
			});
			oops.notFound();
		}
	}

	showConfirmLogin = () => {
		const {
			featureFlags: { confirmLogin },
			rpConfig: { confirmLogin: confirmLoginRP },
		} = this.props;

		return confirmLogin && confirmLoginRP;
	}

	getRedirectURL = () => {
		const { redirectURL, lastOp, casl } = getQuery();
		const [redirectBaseUrl, redirectQueryString] = redirectURL.split('?');
		let redirectQueryObject = parse(redirectQueryString);
		const state = decodeState(redirectQueryObject.state);
		const utm = utmKeys.reduce((map, key) => (
			state[key]
				? { ...map, [key]: state[key] }
				: map
		), {});
		if (Object.keys(utm).length > 0) {
			redirectQueryObject = { ...redirectQueryObject, ...utm };
		}
		if (lastOp !== state.intent) {
			redirectQueryObject.lastOp = lastOp;
		}
		if (casl) {
			redirectQueryObject.casl = casl;
		}
		return `${redirectBaseUrl}?${stringify(redirectQueryObject, { encode: false })}`;
	}

	render() {
		if (this.showConfirmLogin()) {
			return (
				<Layout title="page.title.loginSuccess">
					<h1 className="loginSuccess__page-heading">
						<AutoFormattedMessage id="page.loginSuccess.heading" />
					</h1>
					<hr className="loginSuccess__divider" />
					<p className="loginSuccess__description">
						<AutoFormattedMessage id="page.loginSuccess.successDescription" />
					</p>
					<Link
						theme="button"
						display="block"
						className="loginSuccess__button--submit"
						to={this.getRedirectURL()}
						aria-label={autoFormatMessage('page.loginSuccess.submit.ariaLabel')}
						onClick={() => {
							storageManager.clearSession();
						}}
					>
						<AutoFormattedMessage
							id="page.loginSuccess.completeLoginButton"
						/>
					</Link>
				</Layout>
			);
		}
		return <LoadingPage />;
	}
}

LoginSuccess.propTypes = {
	rpConfig: PropTypes.object.isRequired,
	featureFlags: PropTypes.object.isRequired,
};

export default withRelyingPartyConfig(withFeatureFlags(LoginSuccess));
